import {
  FINTRAC_STR_YES_NO_OPTIONS,
  FINTRAC_STR_TRANSACTION_METHOD_CODES_OPTIONS,
  FINTRAC_STR_TRANSACTION_METHOD_CODES,
  FINTRAC_STR_DATETIME_FORMAT,
  FINTRAC_STR_DATE_FORMAT,
  FINTRAC_STR_TIME_FORMAT,
  FINTRAC_STR_ACTIVITY_SECTOR_CODES,
} from 'app/modules/fintracStr/formConstants';
import { getDependentPaths } from 'app/modules/fintracStr/formUtils';
import { FintracFormSingleField } from 'app/modules/fintracStr/models';
import { isFalse } from 'app/modules/fintracStr/renderConditions';
import { parseFintracDate } from 'app/modules/fintracStr/utils';
import {
  U21FormFieldTypes,
  U21SelectOptionProps,
} from 'app/shared/u21-ui/components';
import { formatDate, formatTime } from 'app/shared/utils/date';
import { isValid, parse } from 'date-fns';

export const FINTRAC_STR_TRANSACTION_SUSPICIOUS_TRANSACTION_DETAILS_FIELDS: FintracFormSingleField =
  {
    type: 'section',
    repeatable: false,
    label: 'Details of Suspicious Transaction',
    name: 'suspiciousTransactionDetails',
    mustExistInPayload: true,
    mustHaveValueIfExist: true,
    isSectionWrapperHidden: true,
    fields: [
      {
        type: U21FormFieldTypes.RADIO_GROUP,
        name: 'attemptedTransactionIndicator',
        label: 'Was the transaction attempted or completed?',
        mustExistInPayload: false,
        mustHaveValueIfExist: false,
        defaultValue: true,
        fieldProps: {
          options: [
            {
              label: 'Attempted',
              value: true,
            },
            {
              label: 'Completed',
              value: false,
            },
          ],
          horizontal: true,
        },
      },
      {
        type: U21FormFieldTypes.TEXTAREA,
        name: 'reasonNotCompleted',
        label: 'Reason transaction was not completed',
        mustExistInPayload: false,
        mustHaveValueIfExist: false,
        dependentPaths: (sectionIndex, fieldSectionIndex) =>
          getDependentPaths(
            undefined,
            [
              `transactions[${sectionIndex}].suspiciousTransactionDetails.attemptedTransactionIndicator`,
            ],
            sectionIndex,
            fieldSectionIndex,
          ),
        hideIfTrue: {
          and: [
            {
              path: (sectionIndex) =>
                `transactions[${sectionIndex}].suspiciousTransactionDetails.attemptedTransactionIndicator`,
              conditions: (value) => isFalse(value),
            },
          ],
        },
      },
      [
        {
          type: U21FormFieldTypes.RADIO_GROUP,
          name: 'hasTimeOfTransaction',
          label:
            'Have you obtained the time of transaction for this transaction?',
          fieldProps: {
            options: FINTRAC_STR_YES_NO_OPTIONS,
            horizontal: true,
          },
          defaultValue: true,
          mustExistInPayload: false,
          mustHaveValueIfExist: false,
          formToPayloadValueConversion: () => {
            return {}; // Override here to prevent this field from being sent to the backend
          },
          payloadToFormValueConversion: (
            parentPath,
            flattenedPayloadValue,
          ) => ({
            hasTimeOfTransaction:
              !!flattenedPayloadValue[`${parentPath}.timeOfTransaction`],
          }),
        },
        {
          type: U21FormFieldTypes.DATE_PICKER,
          name: 'onlyDateOfTransaction', // This is just dateOfTransaction, using a different key to prevent conflict
          label: 'Date of transaction',
          mustExistInPayload: false,
          mustHaveValueIfExist: true,
          fieldProps: {
            minDate: new Date('2001-11-08T00:00:00.000Z'),
            maxDate: new Date(),
          },
          dependentPaths: (sectionIndex, fieldSectionIndex) =>
            getDependentPaths(
              undefined,
              [
                `transactions[${sectionIndex}].suspiciousTransactionDetails.hasTimeOfTransaction`,
              ],
              sectionIndex,
              fieldSectionIndex,
            ),
          hideIfTrue: {
            and: [
              {
                path: (sectionIndex) =>
                  `transactions[${sectionIndex}].suspiciousTransactionDetails.hasTimeOfTransaction`,
                conditions: (value) => !isFalse(value),
              },
            ],
          },
          payloadToFormValueConversion: (parentPath, flattenedPayloadValue) => {
            const dateOfTransaction =
              flattenedPayloadValue[`${parentPath}.dateOfTransaction`];

            // This default response is needed to ensure that the onlyDateOfTransaction key
            // is always present even if the value is undefined or invalid
            // Else, the error will not show up for this field when you click on "Show Section errors"
            const parsed = parseFintracDate(
              typeof dateOfTransaction === 'string'
                ? dateOfTransaction
                : undefined,
            );
            return { onlyDateOfTransaction: parsed };
          },
          formToPayloadValueConversion: (formValue) => {
            if (formValue instanceof Date) {
              return {
                dateOfTransaction: formatDate(
                  formValue,
                  FINTRAC_STR_DATE_FORMAT,
                ),
              };
            }

            return {};
          },
          payloadErrorPathToFormErrorPathConversion: (indexes, isWildcard) => {
            return {
              payloadPath: [
                `transactions[${isWildcard ? '*' : indexes?.[0]}].suspiciousTransactionDetails.dateOfTransaction`,
              ],
              formPath: `transactions[${isWildcard ? '*' : indexes?.[0]}].suspiciousTransactionDetails.onlyDateOfTransaction`,
            };
          },
        },
        {
          type: U21FormFieldTypes.DATE_TIME_PICKER,
          name: 'datetimeOfTransaction', // This is a merge of dateOfTransaction and timeOfTransaction
          label: 'Date and time of transaction',
          mustExistInPayload: false,
          mustHaveValueIfExist: true,
          fieldProps: {
            minDate: new Date('2001-11-08T00:00:00.000Z'),
            maxDate: new Date(),
          },
          dependentPaths: (sectionIndex, fieldSectionIndex) =>
            getDependentPaths(
              undefined,
              [
                `transactions[${sectionIndex}].suspiciousTransactionDetails.hasTimeOfTransaction`,
              ],
              sectionIndex,
              fieldSectionIndex,
            ),
          hideIfTrue: {
            and: [
              {
                path: (sectionIndex) =>
                  `transactions[${sectionIndex}].suspiciousTransactionDetails.hasTimeOfTransaction`,
                conditions: (value) => isFalse(value),
              },
            ],
          },
          payloadToFormValueConversion: (parentPath, flattenedPayloadValue) => {
            const dateOfTransaction =
              flattenedPayloadValue[`${parentPath}.dateOfTransaction`];
            const timeOfTransaction =
              flattenedPayloadValue[`${parentPath}.timeOfTransaction`];

            // This default response is needed to ensure that the datetimeOfTransaction key
            // is always present even if the value is undefined or invalid
            // Else, the error will not show up for this field when you click on "Show Section errors"
            const defaultResponse = {
              datetimeOfTransaction: undefined,
            };

            if (dateOfTransaction && timeOfTransaction) {
              try {
                const parsed = parse(
                  `${dateOfTransaction} ${timeOfTransaction}`,
                  FINTRAC_STR_DATETIME_FORMAT,
                  new Date(),
                );

                return isValid(parsed)
                  ? {
                      datetimeOfTransaction: parsed,
                    }
                  : defaultResponse;
              } catch {
                return defaultResponse;
              }
            }

            return defaultResponse;
          },
          formToPayloadValueConversion: (formValue) => {
            if (formValue instanceof Date) {
              return {
                dateOfTransaction: formatDate(
                  formValue,
                  FINTRAC_STR_DATE_FORMAT,
                ),
                timeOfTransaction: formatTime(
                  formValue,
                  FINTRAC_STR_TIME_FORMAT,
                ),
              };
            }

            return {};
          },
          payloadErrorPathToFormErrorPathConversion: (indexes, isWildcard) => {
            return {
              payloadPath: [
                `transactions[${isWildcard ? '*' : indexes?.[0]}].suspiciousTransactionDetails.dateOfTransaction`,
                `transactions[${isWildcard ? '*' : indexes?.[0]}].suspiciousTransactionDetails.timeOfTransaction`,
              ],
              formPath: `transactions[${isWildcard ? '*' : indexes?.[0]}].suspiciousTransactionDetails.datetimeOfTransaction`,
            };
          },
        },
      ],
      [
        {
          type: U21FormFieldTypes.SELECT,
          name: 'methodCode',
          label: 'Method of transaction',
          dependentPaths: (sectionIndex, fieldSectionIndex) =>
            getDependentPaths(
              undefined,
              ['reportDetails.activitySectorCode'],
              sectionIndex,
              fieldSectionIndex,
            ),
          fieldProps: (flattenedValues) => ({
            options: ((): U21SelectOptionProps<number>[] => {
              const activityCode =
                flattenedValues['reportDetails.activitySectorCode'];
              const inclusionSet = new Set<number>([
                FINTRAC_STR_ACTIVITY_SECTOR_CODES.BANK.value,
                FINTRAC_STR_ACTIVITY_SECTOR_CODES.CAISSE_POPULAIRE.value,
                FINTRAC_STR_ACTIVITY_SECTOR_CODES.CASINO.value,
                FINTRAC_STR_ACTIVITY_SECTOR_CODES.CO_OP_CREDIT_SOCIETY.value,
                FINTRAC_STR_ACTIVITY_SECTOR_CODES.CREDIT_UNION.value,
                FINTRAC_STR_ACTIVITY_SECTOR_CODES.SECURITIES_DEALER.value,
                FINTRAC_STR_ACTIVITY_SECTOR_CODES.TRUST_AND_OR_LOAN_COMPANY
                  .value,
                FINTRAC_STR_ACTIVITY_SECTOR_CODES.CREDIT_UNION_CENTRAL.value,
                FINTRAC_STR_ACTIVITY_SECTOR_CODES.FINANCIAL_SERVICES_COOPERATIVE
                  .value,
              ]);
              const methodCode = new Set<number>([
                FINTRAC_STR_TRANSACTION_METHOD_CODES.NIGHT_DEPOSIT.value,
                FINTRAC_STR_TRANSACTION_METHOD_CODES.QUICK_DROP.value,
              ]);

              return inclusionSet.has(activityCode)
                ? FINTRAC_STR_TRANSACTION_METHOD_CODES_OPTIONS
                : FINTRAC_STR_TRANSACTION_METHOD_CODES_OPTIONS.filter(
                    (option) =>
                      option.value === undefined ||
                      !methodCode.has(option.value),
                  );
            })(),
          }),
          mustExistInPayload: false,
          mustHaveValueIfExist: true,
        },
        {
          type: U21FormFieldTypes.TEXT,
          name: 'methodOther',
          label: 'Description for other method of transaction',
          dependentPaths: (sectionIndex, fieldSectionIndex) =>
            getDependentPaths(
              undefined,
              [
                `transactions[${sectionIndex}].suspiciousTransactionDetails.methodCode`,
              ],
              sectionIndex,
              fieldSectionIndex,
            ),
          hideIfTrue: {
            and: [
              {
                path: (sectionIndex) =>
                  `transactions[${sectionIndex}].suspiciousTransactionDetails.methodCode`,
                conditions: (value) =>
                  value !== FINTRAC_STR_TRANSACTION_METHOD_CODES.OTHER.value,
              },
            ],
          },
          mustExistInPayload: false,
          mustHaveValueIfExist: false,
        },
      ],
      {
        type: U21FormFieldTypes.RADIO_GROUP,
        name: 'isPostingDateDifferentFromTransactionDate',
        label:
          'Is the date and time of posting different from the date and time of transaction?',
        fieldProps: {
          options: FINTRAC_STR_YES_NO_OPTIONS,
          horizontal: true,
        },
        defaultValue: false,
        mustExistInPayload: false,
        mustHaveValueIfExist: false,
        formToPayloadValueConversion: () => {
          return {}; // Override here to prevent this field from being sent to the backend
        },
      },
      [
        {
          type: U21FormFieldTypes.RADIO_GROUP,
          name: 'hasTimeOfPosting',
          label: 'Have you obtained the time of posting for this transaction?',
          fieldProps: {
            options: FINTRAC_STR_YES_NO_OPTIONS,
            horizontal: true,
          },
          defaultValue: true,
          mustExistInPayload: false,
          mustHaveValueIfExist: false,
          dependentPaths: (sectionIndex, fieldSectionIndex) =>
            getDependentPaths(
              undefined,
              [
                `transactions[${sectionIndex}].suspiciousTransactionDetails.isPostingDateDifferentFromTransactionDate`,
              ],
              sectionIndex,
              fieldSectionIndex,
            ),
          hideIfTrue: {
            and: [
              {
                path: (sectionIndex) =>
                  `transactions[${sectionIndex}].suspiciousTransactionDetails.isPostingDateDifferentFromTransactionDate`,
                conditions: (value) => isFalse(value),
              },
            ],
          },
          formToPayloadValueConversion: () => {
            return {}; // Override here to prevent this field from being sent to the backend
          },
          payloadToFormValueConversion: (
            parentPath,
            flattenedPayloadValue,
          ) => ({
            hasTimeOfPosting:
              !!flattenedPayloadValue[`${parentPath}.timeOfPosting`],
          }),
        },
        {
          type: U21FormFieldTypes.DATE_PICKER,
          name: 'onlyDateOfPosting', // This is just dateOfPosting, using a different key to prevent conflict
          label: 'Date of posting',
          mustExistInPayload: false,
          mustHaveValueIfExist: true,
          fieldProps: {
            minDate: new Date('2001-11-08T00:00:00.000Z'),
            maxDate: new Date(),
          },
          dependentPaths: (sectionIndex, fieldSectionIndex) =>
            getDependentPaths(
              undefined,
              [
                `transactions[${sectionIndex}].suspiciousTransactionDetails.hasTimeOfPosting`,
                `transactions[${sectionIndex}].suspiciousTransactionDetails.isPostingDateDifferentFromTransactionDate`,
              ],
              sectionIndex,
              fieldSectionIndex,
            ),
          hideIfTrue: {
            or: [
              {
                path: (sectionIndex) =>
                  `transactions[${sectionIndex}].suspiciousTransactionDetails.hasTimeOfPosting`,
                conditions: (value) => !isFalse(value),
              },
              {
                path: (sectionIndex) =>
                  `transactions[${sectionIndex}].suspiciousTransactionDetails.isPostingDateDifferentFromTransactionDate`,
                conditions: (value) => isFalse(value),
              },
            ],
          },
          payloadToFormValueConversion: (parentPath, flattenedPayloadValue) => {
            const dateOfPosting =
              flattenedPayloadValue[`${parentPath}.dateOfPosting`];

            // This default response is needed to ensure that the onlyDateOfPosting key
            // is always present even if the value is undefined or invalid
            // Else, the error will not show up for this field when you click on "Show Section errors"
            const parsed = parseFintracDate(
              typeof dateOfPosting === 'string' ? dateOfPosting : undefined,
            );
            return { onlyDateOfPosting: parsed };
          },
          formToPayloadValueConversion: (formValue) => {
            if (formValue instanceof Date) {
              return {
                dateOfPosting: formatDate(formValue, FINTRAC_STR_DATE_FORMAT),
              };
            }

            return {};
          },
          payloadErrorPathToFormErrorPathConversion: (indexes, isWildcard) => {
            return {
              payloadPath: [
                `transactions[${isWildcard ? '*' : indexes?.[0]}].suspiciousTransactionDetails.dateOfPosting`,
              ],
              formPath: `transactions[${isWildcard ? '*' : indexes?.[0]}].suspiciousTransactionDetails.onlyDateOfPosting`,
            };
          },
        },
        {
          type: U21FormFieldTypes.DATE_TIME_PICKER,
          name: 'datetimeOfPosting', // This is a merge of dateOfPosting and timeOfPosting
          label: 'Date and time of posting',
          mustExistInPayload: false,
          mustHaveValueIfExist: true,
          fieldProps: {
            minDate: new Date('2001-11-08T00:00:00.000Z'),
            maxDate: new Date(),
          },
          dependentPaths: (sectionIndex, fieldSectionIndex) =>
            getDependentPaths(
              undefined,
              [
                `transactions[${sectionIndex}].suspiciousTransactionDetails.hasTimeOfPosting`,
                `transactions[${sectionIndex}].suspiciousTransactionDetails.isPostingDateDifferentFromTransactionDate`,
              ],
              sectionIndex,
              fieldSectionIndex,
            ),
          hideIfTrue: {
            or: [
              {
                path: (sectionIndex) =>
                  `transactions[${sectionIndex}].suspiciousTransactionDetails.hasTimeOfPosting`,
                conditions: (value) => isFalse(value),
              },
              {
                path: (sectionIndex) =>
                  `transactions[${sectionIndex}].suspiciousTransactionDetails.isPostingDateDifferentFromTransactionDate`,
                conditions: (value) => isFalse(value),
              },
            ],
          },
          payloadToFormValueConversion: (parentPath, flattenedPayloadValue) => {
            const dateOfPosting =
              flattenedPayloadValue[`${parentPath}.dateOfPosting`];
            const timeOfPosting =
              flattenedPayloadValue[`${parentPath}.timeOfPosting`];

            // This default response is needed to ensure that the datetimeOfPosting key
            // is always present even if the value is undefined or invalid
            // Else, the error will not show up for this field when you click on "Show Section errors"
            const defaultResponse = {
              datetimeOfPosting: undefined,
            };

            if (dateOfPosting && timeOfPosting) {
              try {
                const parsed = parse(
                  `${dateOfPosting} ${timeOfPosting}`,
                  FINTRAC_STR_DATETIME_FORMAT,
                  new Date(),
                );

                return isValid(parsed)
                  ? {
                      datetimeOfPosting: parsed,
                    }
                  : defaultResponse;
              } catch {
                return defaultResponse;
              }
            }

            return defaultResponse;
          },
          formToPayloadValueConversion: (formValue) => {
            if (formValue instanceof Date) {
              return {
                dateOfPosting: formatDate(formValue, FINTRAC_STR_DATE_FORMAT),
                timeOfPosting: formatTime(formValue, FINTRAC_STR_TIME_FORMAT),
              };
            }

            return {};
          },
          payloadErrorPathToFormErrorPathConversion: (indexes, isWildcard) => {
            return {
              payloadPath: [
                `transactions[${isWildcard ? '*' : indexes?.[0]}].suspiciousTransactionDetails.dateOfPosting`,
                `transactions[${isWildcard ? '*' : indexes?.[0]}].suspiciousTransactionDetails.timeOfPosting`,
              ],
              formPath: `transactions[${isWildcard ? '*' : indexes?.[0]}].suspiciousTransactionDetails.datetimeOfPosting`,
            };
          },
        },
      ],
      {
        type: U21FormFieldTypes.TEXT,
        name: 'reportingEntityTransactionReference',
        label: 'Reporting entity transaction reference number',
        required: true,
        mustExistInPayload: false,
        mustHaveValueIfExist: true,
      },
      {
        type: U21FormFieldTypes.TEXTAREA,
        name: 'purpose',
        label: 'Purpose of transaction',
        required: false,
        mustExistInPayload: false,
        mustHaveValueIfExist: false,
      },
    ],
  };
